import { useQuery } from 'convex/react';
import { api } from '@formio/convex-api';
import { DocumentOcrConfig, parseDate, icaoToIso2, mapGender } from './document-ocr';
import { DOCUMENT_OCR_CONFIGS } from './document-ocr-configs';

// Transform names → function refs. Convex stores transform as a string
// (configs travel JSON-only); this registry resolves it at render time.
// Adding a new transform: add the function in document-ocr.ts, add an
// entry here, set `transform: "newName"` on the relevant fill in the
// seed-canonical-documents.sh script.
const TRANSFORM_REGISTRY: Record<string, (raw: any) => any> = {
  parseDate,
  icaoToIso2,
  mapGender,
};

interface ConvexDocumentRow {
  key: string;
  name: string;
  expectedDocumentType: string;
  prompt: string;
  fills: Array<{
    sourceKey: string;
    externalId: string;
    displayLabel: string;
    transform?: string;
  }>;
  skipNameVerification?: boolean;
  verificationCriteria?: string;
}

/**
 * Derive the legacy `Record<string, DocumentOcrConfig>` shape from the
 * Convex `documents` rows. Pure — no React — so it's testable.
 *
 * The map key is the Convex `key` (which matches the legacy doc question
 * IDs: passportDocument, nationalIdDocument, etc.). `fieldMapping` is
 * re-derived from `fills` with transform strings resolved via the registry.
 * Unknown transform strings log a warning and are dropped (no transform
 * applied), so a stale seed doesn't crash the wizard.
 */
export function deriveConfigsFromConvex(
  rows: readonly ConvexDocumentRow[],
): Record<string, DocumentOcrConfig> {
  const out: Record<string, DocumentOcrConfig> = {};
  for (const row of rows) {
    const fieldMapping: DocumentOcrConfig['fieldMapping'] = {};
    for (const fill of row.fills) {
      let transform: ((raw: any) => any) | undefined;
      if (fill.transform) {
        transform = TRANSFORM_REGISTRY[fill.transform];
        if (!transform) {
          console.warn(
            `[documents] unknown transform "${fill.transform}" for ${row.key}.${fill.sourceKey} — falling back to identity`,
          );
        }
      }
      fieldMapping[fill.sourceKey] = {
        wizardFieldId: fill.externalId,
        displayLabel: fill.displayLabel,
        transform,
      };
    }
    out[row.key] = {
      documentType: row.key,
      prompt: row.prompt,
      expectedDocumentType: row.expectedDocumentType,
      skipNameVerification: row.skipNameVerification,
      verificationCriteria: row.verificationCriteria,
      fieldMapping,
    };
  }
  return out;
}

/**
 * Hook variant of the loader. Returns the Convex-derived configs once the
 * query resolves; until then (and on query error / empty), falls back to
 * the local `DOCUMENT_OCR_CONFIGS` so the wizard never has a window where
 * doc upload is broken. Eventually `document-ocr-configs.ts` can be
 * deleted once the rollout settles — the fallback is belt-and-suspenders
 * for the transition window.
 */
export function useConvexDocumentOcrConfigs(): Record<string, DocumentOcrConfig> {
  // `api.documents` may not appear in the stale @formio/convex-api types;
  // the runtime path goes through anyApi so it works regardless. Same
  // pattern as api.legalDocuments.getIntakeForClient in Index.tsx.
  const rows = useQuery((api as any).documents.listDocuments, {}) as
    | ConvexDocumentRow[]
    | undefined;
  if (!rows || rows.length === 0) return DOCUMENT_OCR_CONFIGS;
  return deriveConfigsFromConvex(rows);
}
